/**
 * Formal stream lifecycle. Every status change in the API goes through
 * `assertTransition` so callbacks that arrive late, twice or out of order
 * cannot push a stream into an impossible state.
 */
export const STREAM_STATUSES = [
  'CREATED',
  'PREPARING',
  'READY',
  'CONNECTING',
  'LIVE',
  'RECONNECTING',
  'ENDING',
  'ENDED',
  'FAILED',
  'EXPIRED',
] as const;

export type StreamStatus = (typeof STREAM_STATUSES)[number];

export const TERMINAL_STATUSES: ReadonlySet<StreamStatus> = new Set(['ENDED', 'FAILED', 'EXPIRED']);

const TRANSITIONS: Record<StreamStatus, readonly StreamStatus[]> = {
  // Link exists, nothing allocated yet.
  CREATED: ['PREPARING', 'READY', 'CONNECTING', 'ENDING', 'ENDED', 'FAILED', 'EXPIRED'],
  // Ingest capacity is being started.
  PREPARING: ['READY', 'CONNECTING', 'ENDING', 'ENDED', 'FAILED', 'EXPIRED'],
  // Ingest is ready, waiting for the publisher.
  READY: ['CONNECTING', 'ENDING', 'ENDED', 'FAILED', 'EXPIRED'],
  // Publisher connected, first media segment not yet published.
  CONNECTING: ['LIVE', 'RECONNECTING', 'ENDING', 'ENDED', 'FAILED'],
  // First segment and playlist are in storage; viewers can play.
  LIVE: ['RECONNECTING', 'ENDING', 'ENDED', 'FAILED'],
  // Publisher dropped; inside the reconnect grace window.
  RECONNECTING: ['CONNECTING', 'LIVE', 'ENDING', 'ENDED', 'FAILED'],
  // Final playlist being written.
  ENDING: ['ENDED', 'FAILED'],
  ENDED: [],
  FAILED: [],
  EXPIRED: [],
};

export function canTransition(from: StreamStatus, to: StreamStatus): boolean {
  return TRANSITIONS[from].includes(to);
}

export class InvalidTransitionError extends Error {
  constructor(
    readonly from: StreamStatus,
    readonly to: StreamStatus,
  ) {
    super(`Invalid stream transition ${from} -> ${to}`);
  }
}

export function assertTransition(from: StreamStatus, to: StreamStatus): void {
  if (!canTransition(from, to)) throw new InvalidTransitionError(from, to);
}

/** Statuses that hold ingest capacity and count against concurrency limits. */
export const ACTIVE_STATUSES: readonly StreamStatus[] = [
  'CREATED',
  'PREPARING',
  'READY',
  'CONNECTING',
  'LIVE',
  'RECONNECTING',
  'ENDING',
];

/** Statuses whose link still takes video: a publisher may connect, or come back after a drop. */
export const PUBLISHABLE_STATUSES: readonly StreamStatus[] = ['CREATED', 'PREPARING', 'READY', 'CONNECTING', 'LIVE', 'RECONNECTING'];

/** What a broadcaster sees. Never shows "live" before the pipeline confirms it. */
export function broadcasterLabel(status: StreamStatus): string {
  switch (status) {
    case 'CREATED':
    case 'PREPARING':
    case 'READY':
      return 'Preparing...';
    case 'CONNECTING':
      return 'Connecting...';
    case 'LIVE':
      return "You're live!";
    case 'RECONNECTING':
      return 'Reconnecting...';
    case 'ENDING':
    case 'ENDED':
    case 'EXPIRED':
      return 'Stream ended';
    case 'FAILED':
      return 'Something went wrong';
  }
}
